import { useRef, useState } from 'react'
import { Mail, Phone, User as UserIcon } from 'lucide-react'
import type { Member } from '@/api/users'
import { useClickOutside } from '@/lib/useClickOutside'

/** Human-readable role, e.g. 'AGENCY_ADMIN' → 'Agency admin'. */
function roleLabel(role: string): string {
  const s = role.replace(/_/g, ' ').toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/** The member's key details: name + status, role, and work email / phone. */
function OwnerDetails({ member }: { member: Member }) {
  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-brand-darkest truncate">{member.fullName}</span>
        <span
          className={`text-[10px] uppercase rounded-full px-1.5 py-0.5 ${
            member.status === 'active' ? 'bg-success/15 text-success' : 'bg-gray-100 text-gray-500'
          }`}
        >
          {member.status}
        </span>
      </div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 mt-0.5 mb-2">
        {roleLabel(member.role)}
      </div>
      <div className="space-y-1.5 text-sm">
        {member.email ? (
          <a
            href={`mailto:${member.email}`}
            className="flex items-center gap-2 text-brand-dark hover:text-brand-primary break-all"
          >
            <Mail size={13} className="text-gray-400 shrink-0" />
            {member.email}
          </a>
        ) : (
          <span className="flex items-center gap-2 text-gray-400">
            <Mail size={13} className="shrink-0" /> No email
          </span>
        )}
        {member.phone ? (
          <a
            href={`tel:${member.phone}`}
            className="flex items-center gap-2 text-brand-dark hover:text-brand-primary"
          >
            <Phone size={13} className="text-gray-400 shrink-0" />
            {member.phone}
          </a>
        ) : (
          <span className="flex items-center gap-2 text-gray-400">
            <Phone size={13} className="shrink-0" /> No phone on file
          </span>
        )}
      </div>
    </>
  )
}

/**
 * A deal/contact owner. By default a clickable name reveals a small card with the member's role,
 * status and work contact; with {@code inline} the details card is shown expanded (no click) — used
 * on the deal detail so the owner's key details are visible at a glance. "Unassigned" when unset.
 */
/** The member's avatar photo, falling back to a generic icon when absent or broken. */
function MemberAvatar({ member }: { member: Member }) {
  const [broken, setBroken] = useState(false)
  if (member.photoUrl && !broken) {
    return <img src={member.photoUrl} alt="" onError={() => setBroken(true)} className="w-4 h-4 rounded-full object-cover shrink-0" />
  }
  return <UserIcon size={13} className="text-gray-400 shrink-0" />
}

export default function OwnerCard({ member, inline = false }: { member: Member | undefined; inline?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  if (!member) return <span className="text-sm text-gray-400">Unassigned</span>

  if (inline) {
    return <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3"><OwnerDetails member={member} /></div>
  }

  return (
    <div
      ref={ref}
      className="relative inline-block"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title="Owner details"
        className="inline-flex items-center gap-1.5 text-sm text-brand-dark hover:text-brand-primary"
      >
        <MemberAvatar member={member} />
        {member.fullName}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-60 bg-white border border-gray-200 rounded-xl shadow-lg p-3">
          <OwnerDetails member={member} />
        </div>
      )}
    </div>
  )
}
